import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PackageCheck, Search } from 'lucide-react'
import { listReceipts } from '../../api/purchase'
import Spinner from '../../components/Spinner'
import { fmtDay, parseWallClock } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { num } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'
import { useVendorNames } from './purchaseShared'
import { ItemForToggle, useItemFor } from './itemFor'

export default function ReceiptList() {
  const nav = useNavigate()
  const vendorNames = useVendorNames()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const itemFor = useItemFor()

  useEffect(() => {
    listReceipts()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  const vendorOf = (r) => r.vendor_name || vendorNames[String(r.vendor_id)] || ''
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows
      .filter(itemFor.matches)
      .filter((r) => !term || [docNo('receipt', r.receipt_no), docNo('po', r.po_no), vendorOf(r), r.location_name].join(' ').toLowerCase().includes(term))
      .sort((a, b) => (parseWallClock(b.receipt_date)?.getTime() || 0) - (parseWallClock(a.receipt_date)?.getTime() || 0) || num(b.receipt_id) - num(a.receipt_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, vendorNames, itemFor.value])
  const showLocation = rows.some((r) => r.location_name)

  return (
    <div className="ci-page">
      <PurchaseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Receiving</h1>
            <div className="ci-title-meta">Goods that have arrived from vendors against your purchase orders.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to={`/purchase/receipts/new${itemFor.query}`}><PackageCheck size={15} />Receive goods</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search receipts">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Receipt, PO or vendor…" />
        </label>
        <ItemForToggle itemFor={itemFor} rows={rows} />
      </div>

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Received</th>
                <th>Order</th>
                <th>Vendor</th>
                {showLocation && <th>Location</th>}
                <th className="num">Items</th>
                <th className="num">Returned</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7"><Spinner label="Loading receipts…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="7"><div className="ci-empty">{rows.length ? 'No receipts match.' : 'Nothing has been received yet.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.receipt_id} className="row-link" onClick={() => nav(`/purchase/receipts/${r.receipt_id}`)}>
                  <td><Link className="ci-je-link" to={`/purchase/receipts/${r.receipt_id}`} onClick={(e) => e.stopPropagation()}>{docNo('receipt', r.receipt_no)}</Link></td>
                  <td className="ci-muted-cell">{fmtDay(r.receipt_date) || '–'}</td>
                  <td>{r.po_id ? <Link className="ci-je-link" to={`/purchase/po-detail/${r.po_id}`} onClick={(e) => e.stopPropagation()}>{docNo('po', r.po_no)}</Link> : '–'}</td>
                  <td className="ci-strong">{vendorOf(r) || '–'}</td>
                  {showLocation && <td>{r.location_name || '–'}</td>}
                  <td className="num">{num(r.line_count)}</td>
                  <td className="num">{num(r.returned_qty) ? num(r.returned_qty).toLocaleString() : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'receipt' : 'receipts'}</span>
        </div>
      </div>
    </div>
  )
}
